import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@radix-ui/themes'
import * as turf from '@turf/turf'
import X from 'assets/x.svg?react'
import { EditButton } from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { useRolodexEdit } from 'features/fde'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoUnit, RolodexEntryJson } from 'types'

import { LocationMap } from './LocationMap'

const StyledDialogOverlay = styled(Dialog.Overlay)`
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
`

const StyledDialogContent = styled(Dialog.Content)`
  background: ${({ theme }) => theme.forecast.cardBackground};
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 8px;
  padding: 20px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
`

const StyledDialogTitle = styled(Dialog.Title)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`

const StyledDialogX = styled(Dialog.Close)`
  cursor: pointer;
`

const CloseIcon = styled(X)`
  width: 16px;
  height: 16px;
`

const MapContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  align-self: stretch;
  height: 300px;
  width: 400px;
  margin-bottom: 12px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const LatLongContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledFieldset = styled.div`
  display: flex;
  font-size: 14px;
  line-height: 20px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.forecast.defaultText};
  padding-left: 6px;
  width: 190px;
`

const StyledLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StyledInput = styled.input`
  font-size: 14px;
  line-height: 20px;
  background-color: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.forecast.defaultText};
  appearance: auto;
`

const StyledDialogDescription = styled(Dialog.Description)`
  font-size: 14px;
  line-height: 20px;
  padding: 8px 0px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`

const StyledCloseButton = styled(Button)`
  cursor: pointer;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  padding: 2px 8px 2px 8px;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

export const EditLocationModal: React.FC<{
  unitName: string | undefined
  unit: GeckoUnit | undefined
  forceRefresh: () => Promise<void>
}> = ({ unitName, unit, forceRefresh }) => {
  const { entryLatitude, entryLongitude } = useMemo(() => {
    if (!unit?.data.geography) {
      return {}
    }

    const point = turf.centroid(unit.data.geography)
    const [entryLongitude, entryLatitude] = point.geometry.coordinates
    return {
      entryLatitude,
      entryLongitude,
    }
  }, [unit])

  const [inputLatitude, setInputLatitude] = useState<number | undefined>()
  const [inputLongitude, setInputLongitude] = useState<number | undefined>()
  const geography = useMemo<turf.Point | undefined>(() => {
    const long = inputLongitude ?? entryLongitude
    const lat = inputLatitude ?? entryLatitude
    if (long === undefined || lat === undefined) return undefined
    return {
      type: 'Point',
      coordinates: [long, lat],
    }
  }, [inputLongitude, entryLongitude, inputLatitude, entryLatitude])

  const { rolodexPut } = useRolodexEdit()

  const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    if (val < -90) {
      setInputLatitude(-90) // Sets to minimum latitude if below -90
    } else if (val > 90) {
      setInputLatitude(90) // Sets to maximum latitude if above 90
    } else {
      setInputLatitude(val)
    }
  }

  const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    if (val < -180) {
      setInputLongitude(-180) // Sets to minimum longitude if below -180
    } else if (val > 180) {
      setInputLongitude(180) // Sets to maximum longitude if above 180
    } else {
      setInputLongitude(val)
    }
  }

  const handleMarkerDragEnd = (longitude: number, latitude: number) => {
    setInputLongitude(longitude)
    setInputLatitude(latitude)
  }

  const handleSaveChange = useCallback(() => {
    if (inputLatitude !== undefined && inputLongitude !== undefined && unit) {
      const rawUnit: RolodexEntryJson = unit.raw
      const newPoint = turf.point([inputLongitude, inputLatitude])
      rawUnit.data.geography = newPoint.geometry as turf.helpers.Geometry
      rolodexPut([rawUnit]).then(() => {
        forceRefresh()
      })
    }
  }, [forceRefresh, inputLatitude, inputLongitude, rolodexPut, unit])

  const handleDeleteLocation = useCallback(() => {
    if (unit && unit.data.geography) {
      const rawUnit: RolodexEntryJson = unit.raw
      if (rawUnit.data.geography) {
        delete rawUnit.data.geography
      }
      rolodexPut([rawUnit]).then(() => {
        forceRefresh()
      })
    }
  }, [forceRefresh, rolodexPut, unit])

  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset on close
      setInputLatitude(undefined)
      setInputLongitude(undefined)
    }
  }

  return (
    <Dialog.Root onOpenChange={handleDialogOpenChange}>
      <CantileverCard.HiddenUnlessHovering>
        <Dialog.Trigger asChild>
          <EditButton ghost />
        </Dialog.Trigger>
      </CantileverCard.HiddenUnlessHovering>
      <Dialog.Portal>
        <StyledDialogOverlay />
        <StyledDialogContent
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}>
          <HeaderRow>
            <StyledDialogTitle>Edit Location</StyledDialogTitle>
            <StyledDialogX asChild>
              <CloseIcon />
            </StyledDialogX>
          </HeaderRow>
          <MapContainer>
            {geography ? (
              <LocationMap
                geography={geography}
                draggable={true}
                onMarkerDragEnd={handleMarkerDragEnd}
              />
            ) : (
              <LocationMap geography={geography} draggable={true} />
            )}
          </MapContainer>
          <LatLongContainer>
            <StyledFieldset>
              <StyledLabel>Latitude:</StyledLabel>
              <StyledInput
                prefix='Latitude:'
                value={inputLatitude || entryLatitude || ''}
                min={-90}
                max={90}
                step={0.00001}
                onChange={handleLatitudeChange}
                type='number'
              />
            </StyledFieldset>
            <StyledFieldset>
              <StyledLabel>Longitude:</StyledLabel>
              <StyledInput
                prefix='Longitude:'
                value={inputLongitude || entryLongitude || ''}
                min={-180}
                max={180}
                step={0.00001}
                onChange={handleLongitudeChange}
                type='number'
              />
            </StyledFieldset>
          </LatLongContainer>
          <StyledDialogDescription>
            Editing location for: {unitName}
          </StyledDialogDescription>
          <ButtonsContainer>
            <Dialog.Close asChild>
              <StyledCloseButton>Cancel</StyledCloseButton>
            </Dialog.Close>
            <Dialog.Close asChild>
              <StyledCloseButton onClick={handleDeleteLocation}>
                Delete Location
              </StyledCloseButton>
            </Dialog.Close>
            <Dialog.Close asChild>
              <StyledCloseButton onClick={handleSaveChange}>
                Save
              </StyledCloseButton>
            </Dialog.Close>
          </ButtonsContainer>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
